import { FileWithPath } from '@mantine/dropzone';
import posthog from 'posthog-js';
import { AuthService } from 'services/auth-service';
import { NotificationService } from 'services/notification-service';
import { OrganisationService } from 'services/organisation-service';
import { AuthUpdatePasswordDto, UpdateAvatarDto, User, UserAccess, UserAccessMap } from 'shared/interfaces/User';
import { axiosBaseInstance } from 'shared/utils/axios-instances';
import { queryClientSingleton } from 'shared/utils/query-client-singleton';

const USER_ACCESS_ROLE_KEY = 'userAccessRole';
const IS_CUSTOM_ORGANISATION_KEY = 'isCustomOrganisation';
const userBaseEndpoint = '/user';

export const UserService = {
  userInfo: async (): Promise<User> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: userBaseEndpoint
    });

    setUserAccessRole(response.data.role);

    const organisationStatus = await OrganisationService.getOrganisationStatus();
    response.data.isInCustomOrganisation = organisationStatus.isCustom;
    response.data.hasCompletedOnboarding = organisationStatus.hasCompletedOnboarding;
    setIsCustomOrganisation(organisationStatus.isCustom);

    return response.data;
  },

  hasUserAccessWithMinRole: (minRole: UserAccess): boolean => {
    const userAccessRole = getUserAccessRole();
    return userAccessRole !== UserAccess.NULL && userAccessRole >= minRole;
  },

  isCustomOrganisationMember: (): boolean => {
    const isCustomOrganisation = localStorage.getItem(IS_CUSTOM_ORGANISATION_KEY);
    return isCustomOrganisation ? JSON.parse(isCustomOrganisation) : false;
  },

  logout: () => {
    queryClientSingleton.clear();
    AuthService.clearAuthSession();
    clearUserAccessRole();
    clearIsCustomOrganisation();

    // Clear all keys except locale
    clearAllKeysExceptLocale();

    // Clear Posthog session identification
    posthog.reset();
  },

  setOtherUserAccessRole: (currentUser: User, selectedUserRole: string | null) => {
    if (UserService.hasUserAccessWithMinRole(UserAccess.ADMIN) && selectedUserRole !== null) {
      const roleValue = UserAccessMap.get(selectedUserRole);
      if (roleValue !== undefined) {
        // This check blocks a user of same access rights to assign a user with the same access rights
        if (roleValue === currentUser.role) {
          NotificationService.showResult(false, 'FEED_BACK.USERS.FAILURE.UPDATE.NO_RIGHTS');
          return;
        }
        return roleValue;
      }
    }
    NotificationService.showResult(false, 'FEED_BACK.USERS.FAILURE.UPDATE.NO_RIGHTS');
  },

  uploadCustomAvatar: async (file: FileWithPath | undefined): Promise<UpdateAvatarDto> => {
    if (!file) return Promise.reject('No file provided');
    const fileData = new FormData();
    fileData.append('file', file);

    const response = await axiosBaseInstance({
      method: 'put',
      url: `${userBaseEndpoint}/custom-avatar`,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: fileData
    });

    return response.data;
  },

  removeAvatar: async (): Promise<UpdateAvatarDto> => {
    const response = await axiosBaseInstance({
      method: 'put',
      url: `${userBaseEndpoint}/remove-avatar`
    });

    return response.data;
  },

  updatePassword: async (updatePasswordDto: AuthUpdatePasswordDto): Promise<void> => {
    await axiosBaseInstance({
      method: 'put',
      url: `${userBaseEndpoint}/update-password`,
      data: {
        ...updatePasswordDto,
        email: updatePasswordDto.email.toLowerCase()
      }
    });
  }
};

const getUserAccessRole = (): UserAccess => {
  const userAccessRole = localStorage.getItem(USER_ACCESS_ROLE_KEY);
  return userAccessRole ? JSON.parse(userAccessRole) : UserAccess.NULL;
};

const setUserAccessRole = (userAccess: UserAccess) =>
  localStorage.setItem(USER_ACCESS_ROLE_KEY, JSON.stringify(userAccess));

const setIsCustomOrganisation = (isCustomOrganisation: boolean) =>
  localStorage.setItem(IS_CUSTOM_ORGANISATION_KEY, JSON.stringify(isCustomOrganisation));

const clearIsCustomOrganisation = () => localStorage.removeItem(IS_CUSTOM_ORGANISATION_KEY);

const clearUserAccessRole = () => localStorage.removeItem(USER_ACCESS_ROLE_KEY);

const clearAllKeysExceptLocale = () => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key !== null && key !== 'locale') {
      localStorage.removeItem(key);
    }
  }
};
